/**
 * Legacy placeholder substitution (db4mcreator substitute_variables / send_content):
 *
 *   %ILANG_key%      → internal text
 *   %LANG_key%       → business text (global < database < table)
 *   %LANG_key.DE%    → global text of an explicit language (load_language_s)
 *
 * Unknown placeholders are removed like legacy does (`keepUnknown` keeps them) and reported.
 * Unlike legacy (single pass), placeholders inside substituted texts are resolved too, up to
 * `maxDepth` levels (cycles end there with a warning).
 *
 * Pure module (no node: imports) – the web client uses the same code with the dictionaries
 * served by GET /api/v2/i18n/{locale}.
 */
import type { LegacyHash } from './scopes.js';

export type Dictionary = Readonly<Record<string, string>>;

/** The two legacy text hashes, already merged in precedence order for one locale. */
export interface Dictionaries {
  LANG: Dictionary;
  ILANG: Dictionary;
}

export interface SubstituteOptions {
  /** nesting depth for placeholders inside texts (default 5) */
  maxDepth?: number;
  /** `%LANG_key.XX%`: text of `key` in the global scope of language `xx` (lower case) */
  explicit?: (key: string, locale: string) => string | undefined;
  /** keep unknown placeholders verbatim instead of removing them (legacy removes them) */
  keepUnknown?: boolean;
  /** receives unknown keys (`LANG_x`, `ILANG_x`, `LANG_x.DE`) and depth problems */
  onWarning?: (warning: I18nWarning) => void;
}

export interface I18nWarning {
  code: 'unknown' | 'depth';
  /** the placeholder without the % signs, e.g. `LANG_GAME_STATUS` */
  placeholder: string;
}

/** Matches %LANG_x%, %ILANG_x% and %LANG_x.XX%. */
export const LANG_PLACEHOLDER = /%(I?LANG)_([A-Za-z0-9_]+)(?:\.([A-Z]{2}))?%/g;

const own = (d: Dictionary, k: string): string | undefined => (Object.prototype.hasOwnProperty.call(d, k) ? d[k] : undefined);

/** Does the text contain a %LANG/%ILANG placeholder? */
export function hasPlaceholder(text: string): boolean {
  LANG_PLACEHOLDER.lastIndex = 0;
  const r = LANG_PLACEHOLDER.test(text);
  LANG_PLACEHOLDER.lastIndex = 0;
  return r;
}

/** Look up a key in one hash. */
export function lookup(dicts: Dictionaries, hash: LegacyHash, key: string): string | undefined {
  return own(dicts[hash], key);
}

/** Substitute %LANG_x% / %ILANG_x% / %LANG_x.XX% placeholders in `text`. */
export function substitute(text: string, dicts: Dictionaries, opts: SubstituteOptions = {}): string {
  const maxDepth = opts.maxDepth ?? 5;
  const run = (s: string, depth: number): string => {
    if (!s.includes('%')) return s;
    return s.replace(LANG_PLACEHOLDER, (m, hash: LegacyHash, key: string, lang: string | undefined) => {
      const ph = m.slice(1, -1);
      let v: string | undefined;
      if (lang !== undefined) {
        if (hash === 'ILANG') return m; // not a legacy placeholder: left alone like legacy
        v = opts.explicit?.(key, lang.toLowerCase());
      } else v = own(dicts[hash], key);
      if (v === undefined) {
        opts.onWarning?.({ code: 'unknown', placeholder: ph });
        return opts.keepUnknown ? m : '';
      }
      if (!v.includes('%')) return v;
      if (depth >= maxDepth) {
        opts.onWarning?.({ code: 'depth', placeholder: ph });
        return v.replace(LANG_PLACEHOLDER, (n) => (opts.keepUnknown ? n : ''));
      }
      return run(v, depth + 1);
    });
  };
  return run(text, 0);
}

/** Merge dictionaries; later ones override earlier ones (legacy `%LANG = (%LANG, …)`). */
export function mergeDictionaries(...dicts: readonly (Dictionary | undefined)[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const d of dicts) {
    if (!d) continue;
    for (const [k, v] of Object.entries(d)) Object.defineProperty(out, k, { value: v, enumerable: true, writable: true, configurable: true });
  }
  return out;
}

/** A translate function bound to dictionaries (warnings go to `opts.onWarning`). */
export function translator(dicts: Dictionaries, opts: SubstituteOptions = {}): (text: string) => string {
  return (text) => substitute(text, dicts, opts);
}
